import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import { skip, takeUntil, tap } from 'rxjs/operators';
import { GRANULARITY_OPTIONS, HIGHCHART_OPTIONS_DEFAULT, newQueryDefaultParams } from 'app/data-studio-redesign/services/data-analysis-helper';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { cloneDeep } from 'lodash';
import { DataStudioService } from 'app/shared/services/data-services/data-studio/data-studio.service';
import { IDataStudioPark, IDataStudioQuery, IYAxis, ISeries, DEVICE_TYPES } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { uniqWith, isEqual } from 'lodash';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { DataAnalysisService } from 'app/data-studio-redesign/services/data-analysis-service';
import moment from 'moment';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import { isNil } from 'lodash';

@Component({
    selector: 'app-time-series',
    templateUrl: './time-series.component.html',
    styleUrls: ['./time-series.component.scss'],
    standalone: false
})
export class TimeSeriesComponent implements OnInit, OnChanges, OnDestroy  {

  @Input() queryId: string;
  @Input() view: number;
  @Input() queryDetails;
  @Input() myQueriesView: boolean;
  @Input() parks: IDataStudioPark[];
  @Input() isReport: boolean;
  @Output() back = new EventEmitter();
  @Output() updateQueriesList = new EventEmitter();

  @ViewChild('newseriescard', { static: false }) newseriescard: ElementRef;

  public granOptions: { names: string[], values: string[] };
  public granLabel: string;
  public granValue: string;
  public periodLabel: string;
  public searchValue: string;
  public startTs: number;
  public endTs: number;
  public oldStartTs: number;
  public oldEndTs: number;
  public creatingSeries: boolean;
  public series: ISeries[];
  public signalsData: { data: ISeries[], yAxis: IYAxis[] } = { data: [], yAxis: [] };
  public data: IDataStudioQuery;
  public lastIndex: number;
  public loadingUpdate: boolean;
  public addSeriesList: ISeries[];
  public removeSeriesList: ISeries[];
  public updateSeriesList: ISeries[];
  public filteredSeries: ISeries[];
  public selectedDeleteList: ISeries[];
  public removeItems: { ids: string[], yAxis: IYAxis[] };
  public updateFlag: boolean;
  public yAxisList: IYAxis[] = [];
  public views: string[];
  public activeView: number;
  public confirmDelete: boolean;
  public modifyTitleMode: boolean;
  public loadingSerie: boolean;
  public loadingChart: boolean = true;
  public loadingSignalList: boolean = true;
  public seriesIds: string[];
  public allSeriesUpdateMode: boolean = false;
  public oldGranValue: string;
  public oldGranLabel: string;
  public granChanged: boolean;
  public periodChanged: boolean;
  public oldPeriodLabel: string;
  public savingData = false;
  public hasValidData = false;

  public updateGranularity: boolean = false;
  private modalRef: BsModalRef;
  public exportOptions = ['.xlsx', '.csv','image', 'pdf'];
  public exportChart$: ReplaySubject<number> = new ReplaySubject(null);

  public theme$: Observable<string>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private signalParkmap: any = {
    productionScada: 'PRODUCTION_SCADA',
    productionGrid: 'PRODUCTION_GRID',
    electricityLosses: 'ELECTRICITY',
    consumptionGrid: 'ENERGY_CONSUMPTION',
    electricitySalesRevenue: 'REV_ELEC',
    incentiveRevenue: 'REV_INC',
    totalRevenue: 'TOTAL_REV',
    consumptionLosses: 'ENERGY_CONSUMP',
    marketPrice: 'AVG_MARKET_ELEC',
    PPAElectricityPrice:'AVG_PPA_ELEC',
    temperature: 'AVG_TEMPERATURE',
    windSpeed: 'AVG_WIND_SPEED',
    theoreticalProduction: 'THEORETICAL_PRODUCTION'
  }

  public readonly MAX_SERIES_TO_DISPLAY: number = 50;

  constructor(
    private snackBar: MatSnackBar,
    private dataStudioService: DataStudioService,
    private dataStudioRedesignService: DataStudioRedesignService,
    private modalService: BsModalService,
    private themeService: ThemeService,
    private dataAnalysisService: DataAnalysisService,
    private timezoneService: TimezoneService,
  ) { }

  public ngOnInit(): void {
    this.theme$ = this.themeService.getTheme();
    this.timezoneService.getTimezoneObservable().pipe(skip(1), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.applySeriesUpdate(true);
    })

  }

  public ngOnChanges(changes): void {
    if (changes.view) {
      this.activeView = this.view
      this.initComponent();
    }
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  public selectGran({option, index}) {
    this.allSeriesUpdateMode = true;
    this.oldGranLabel = this.granLabel;
    this.oldGranValue = this.granValue;
    this.granLabel = option;
    this.granValue = this.granOptions.values[index];
    this.granChanged = true;
    this.series.forEach(s => {
      if (this.selectedDeleteList.map(d => d.id).includes(s.id)) {
        s.granularity = this.granValue;
      }
    })
    this.filteredSeries.forEach(s => {
      if (this.selectedDeleteList.map(d => d.id).includes(s.id)) {
        s.granularity = this.granValue;
      }
    })
    this.applySeriesUpdate();
  }

  public cancelSeriesUpdate() {
    this.allSeriesUpdateMode = false;
    if (this.granChanged) {
      this.granLabel = this.oldGranLabel;
      this.granValue = this.oldGranValue;
    }

    if (this.periodChanged) {
      this.startTs = this.oldStartTs;
      this.endTs = this.oldEndTs;
      this.periodLabel = this.oldPeriodLabel;
    }

    this.granChanged = false;
    this.periodChanged = false;
  }

  private checkValidGranularity(): void {
    const availableGranularities = this.filterGranularityOptions();
    this.series.forEach(s => {
      if (!availableGranularities.values.includes(s.granularity)) {
        s.granularity = availableGranularities.values[availableGranularities.values.length - 1];
      }
    })
  } 

  public applySeriesUpdate(isTimezoneChange: boolean = false) {
    if (!isTimezoneChange) {
      this.updateFlag = true;
    }
    this.allSeriesUpdateMode = false;
    this.loadingUpdate = true;
    this.checkValidGranularity();
    const chartData$ = this.filteredSeries?.map(
      item => this.getSignalData(
        { assetId: item.assetId, name: item.parkName, deviceType: item.deviceType },
        item.signal,
        item.granularity,
        item.queryData,
        this.startTs,
        this.endTs)
    );
    combineLatest(chartData$)
    .subscribe((data: any[]) => {
      this.updateSeriesList = this.series.filter(serie => this.filteredSeries.find(f => f.id === serie.id))
      .map((signal, index) => {
        const keyFieldName = this.getKeyFieldName(signal, data[index]);

        if (data[index].length) {
          signal.yAxis = this.yAxisList.findIndex(item => item.title.text === signal.unit);
          signal.chartData = data[index].every(d => isNil(d[keyFieldName])) ? [] : data[index].map(item => [this.timezoneService.unApplyTzFromDate(item.Timestamp).valueOf(), item[keyFieldName]]);
        }
        return signal
      });
      this.series = JSON.parse(JSON.stringify(this.series));
      this.filteredSeries = this.series;
      this.loadingUpdate = false;
      this.granChanged = false;
      this.periodChanged = false;
      this.dataAnalysisService.timeSeriesPeriodChange$.next({startTs: this.startTs, endTs: this.endTs});
      this.granOptions = this.filterGranularityOptions();
      this.signalsData = {
        data: this.series.filter(item => item.visible),
        yAxis: this.yAxisList
      };
      this.hasValidData = this.validatedData();
    });
  }

  public addSeries() {
    this.creatingSeries = true;
    setTimeout(() => {
      this.newseriescard.nativeElement.scrollIntoView({behavior: 'smooth'});
    }, 100);
  }

  public update() {
    this.updateFlag = true;
    this.loadingUpdate = true;
    this.updateGranularity = false;
    this.updateSeriesList?.length && this.updateSeries();
  }

  public closeNew() {
    this.creatingSeries = false;
  }

  public createNew(series: ISeries[]) {
    series.forEach((item, index) => {
      if (index + this.series?.filter(item => item.visible).length <= this.MAX_SERIES_TO_DISPLAY) {
        item.visible = true;
      }
      this.addSeriesList.push(item);
      return item;
    });
    this.addDataNewSeries();
    this.series = [...series, ...this.series || []];
    this.seriesIds = this.series.map(serie => serie.id);
    this.filteredSeries = this.series;
    this.closeNew();
  }

  public searchString(input) {
    this.searchValue = input;
    this.filteredSeries = this.series.filter(item => {
      return item.parkName.toLocaleLowerCase().includes(input.toLocaleLowerCase().trim()) ||
        item.device.toLocaleLowerCase().includes(input.toLocaleLowerCase().trim()) ||
        item.signalName.toLocaleLowerCase().includes(input.toLocaleLowerCase().trim())
    });
  }

  public save() {
    this.data = {
      ...this.data,
      startTs: this.startTs,
      endTs: this.endTs,
      period: this.periodLabel,
      granularity: this.granLabel,
      series: [...this.series.map(item => {
        const yAxis = this.setYAxis(this.series);
        const index = yAxis.findIndex(el => el.title?.text === item.unit);
        item.yAxis = index;
        item.newItem = false;
        return item})
      ]
    };

    const query = {
      _id: (this.queryId && this.myQueriesView) ? this.queryId : undefined,
      queryNumber: (this.queryId && this.myQueriesView) ? this.data.id : undefined,
      title: this.data.name,
      period: this.periodLabel,
      startTs: this.startTs,
      endTs: this.endTs,
      queryType: 'TIMESERIES',
      isSharedQuery: false,
      granularity: this.granLabel,
      series: this.series.map(serie => ({
        parkId: serie.parkId,
        deviceType: serie.deviceType,
        assetId: serie.assetId,
        signal: serie.signal,
        signalName: serie.signalName,
        signalUnit: serie.unit,
        yAxisMin: serie.yAxisMin,
        yAxisMax: serie.yAxisMax,
        granularity: serie.granularity,
        visible: serie.visible,
        aggregation: 'AVG',
        colorHex: serie.color,
        graphType: serie.chartTypeName,
        queryDataType: serie.queryData,
        graphDataType: serie.type,
        graphDataMarker: {
          symbol: serie.marker?.symbol
        },
        graphIsStep: serie.step,
        graphDashStyle: serie.dashStyle
      }))
    };
    if (this.modifyTitleMode) {
      this.modifyTitle(false)
    }
    (this.queryId && this.myQueriesView) ? this.updateQuery(query) : this.createQuery(query);
    this.confirmDelete = false;
  }

  public cancel() {
    this.closeModal();
    this.signalsData = {data: [], yAxis: []};
    this.hasValidData = false;
    this.deleteSeries({series: this.series, confirm: false});
    this.loadingChart = true;
    this.updateFlag = false;
    this.loadingSignalList = true;
    this.initComponent();
  }

  public modifyTitle(value) {
    this.modifyTitleMode = value;
    if (!value) {
      this.updateFlag = true;
    }
  }

  public delete() {
    const { id } = this.data;
    this.dataStudioService.deleteQuery(id);
  }

  public changeVisibility(row) {
    this.updateFlag = true;
    if (row.visible) {
      if (this.filteredSeries.filter(item => item.visible)?.length <= this.MAX_SERIES_TO_DISPLAY) {
        this.addSeriesList.push(row);
      } else {
        this.snackBar.open(`Maximum series to show - ${this.MAX_SERIES_TO_DISPLAY}`, '', {duration: 3000});
        row.visible = false;
      }
    } else {
      const item = this.addSeriesList.find(item => item.id === row.id);
      if (item) {
        this.addSeriesList = this.addSeriesList.filter(item => item.id !== row.id)
      }
    }

    const serie = this.series.find(s => s.id === row.id)
    serie.visible = row.visible;
    if (serie.visible) {
      this.dataAnalysisService.timeSeriesSignalAdd$.next(true);
      if (!this.yAxisList.map(l => l.title.text).includes(serie.unit)) {
        const newYAxis = this.setYAxisValue(serie, this.yAxisList.length > 0)
        this.yAxisList.push(newYAxis);
      }
    } else {
      const yAxis = this.setYAxis(this.series.filter(s => !s.id.includes(serie.id)));
      this.yAxisList = yAxis;
      this.dataAnalysisService.timeSeriesSignalRemove$.next({ids: [serie.id], yAxis });
    }
    this.signalsData = {
      data: this.series.filter(item => item.visible),
      yAxis: this.yAxisList
    };
    this.hasValidData = this.validatedData();
    this.updateYAxisMaxAndMin();
    this.loadingUpdate = false;
  }

  public updateSerie({type, serie, dataUpdate}, emitValue: boolean = true) {
    this.updateFlag = true;
    if (dataUpdate) {
      this.updateGranularity = true;
      this.loadingUpdate = true;
      this.getSignalData(
        { assetId: serie.assetId, name: serie.parkName, deviceType: serie.deviceType },
        serie.signal,
        serie.granularity,
        serie.queryData,
        this.startTs,
        this.endTs
      ).subscribe((data: any[]) => {
        const keyFieldName = this.getKeyFieldName(serie, data);
        serie.chartData = data.map((item) => [this.timezoneService.unApplyTzFromDate(item.Timestamp).valueOf(), item[keyFieldName]]);

        this.updateList(serie);
        if (emitValue) {
          this.dataAnalysisService.timeSeriesSignalChange$.next(serie);
        }
        this.loadingUpdate = false;
      })
    } else {
      this.updateList(serie);
      if (type === 'color' || type === 'seriesType') {
        this.dataAnalysisService.timeSeriesSignalChange$.next(serie);
      }
    }
  }

  private updateList(serie) {
    const index = this.series.findIndex(item => item.id === serie.id);
    if (serie.visible) {
      if (index > -1) {
        Object.assign(this.series[index], serie);
        this.filteredSeries = this.series;
      }
      const indexUpd = this.updateSeriesList.findIndex(item => item.id === serie.id);
      indexUpd > -1 ? Object.assign(this.updateSeriesList[indexUpd], serie) : this.updateSeriesList.push(serie);
    }
  }

  public deleteSeries({series, confirm}) {
    this.confirmDelete = confirm && series.length;
    this.selectedDeleteList = series;
    this.granOptions = this.filterGranularityOptions();
    
    if (!confirm) {
      this.deleteSelected();
    }
  }

  public deleteSelected() {
    this.updateFlag = true;
    const ids = this.selectedDeleteList.map(item => item.id);
    const yAxis = this.setYAxis(this.series.filter(s => !ids.includes(s.id)));
    this.removeItems = { ids, yAxis };
    this.series = this.series.filter(serie => !ids.includes(serie.id));
    this.signalsData = {
      data: this.series.filter(serie => !ids.includes(serie.id)),
      yAxis
    };
    this.addSeriesList = this.addSeriesList.filter(item => !ids.includes(item.id));
    this.hasValidData = this.validatedData();
    this.seriesIds = this.series.map(serie => serie.id);
    this.dataAnalysisService.timeSeriesSignalRemove$.next(this.removeItems);
    this.filteredSeries = this.series;
    this.confirmDelete = false;
  }

  public chartAxisUpdated() {
    this.updateYAxisMaxAndMin();
    this.updateFlag = true;
  }

  public updated() {
    this.loadingUpdate = false;
    this.updateSeriesList.length = 0;
  }

  public updateDate({startTs, endTs, period}) {
    this.allSeriesUpdateMode = true;
    this.oldPeriodLabel = this.periodLabel;
    this.periodLabel = period;
    this.oldStartTs = this.startTs;
    this.oldEndTs = this.endTs;
    this.startTs = startTs;
    this.endTs = endTs;
    this.periodChanged = true;
    this.applySeriesUpdate();
  }

  public backToQueriesList() {
    this.closeModal();
    this.back.emit();
  }

  public selectedView(index) {
    if (this.loadingUpdate) return;
    if (index) {
      this.updateSeriesList = [];
    } else {
      this.yAxisList  = this.setYAxis(this.series.filter(item => item.visible));
      this.signalsData = {
        data: this.series.filter(item => item.visible),
        yAxis: this.yAxisList
      };
    }
    this.hasValidData = this.validatedData();
    this.activeView = index;
    this.exportChart$.next(null);
  }

  private initComponent() {
    this.lastIndex = 0;
    this.loadingUpdate = false;
    this.granOptions = this.filterGranularityOptions();
    this.addSeriesList = [];
    this.removeSeriesList = [];
    this.updateSeriesList = [];
    this.views = ['BUTTONS.CHART', 'BUTTONS.TABLE'];
    this.confirmDelete = false;
    this.modifyTitleMode = false;
    this.queryId ? this.getQueryData() : this.setDefaultData();
  }

  private getKeyFieldName(signal, signalsValues) {
    if (!signalsValues[0]) return;
    const translatedSignalName = `SIGNALS.${signal.queryData === 'parkdata' ? this.signalParkmap[signal.signal] : signal.signal }.DISPLAY_NAME(${signal.unit})`;

    if (translatedSignalName in signalsValues[0]) return translatedSignalName;

    const keyFieldName = `${signal.signalName} (${signal.unit})`;
    if (keyFieldName in signalsValues[0]) return keyFieldName;
    if (signal.signal in signalsValues[0]) return signal.signal;
  }

  private addDataNewSeries() {
    this.updateFlag = true;
    this.loadingSerie = true;
    this.getListObs(this.addSeriesList).subscribe((data: any[]) => {
      this.addSeriesList.forEach((signal, index) => {
        const keyFieldName = this.getKeyFieldName(signal, data[index]);

        if (data[index].length && keyFieldName) {
          if (!this.yAxisList.map(l => l.title.text).includes(signal.unit)) {
            const newYAxis = this.setYAxisValue(signal, this.yAxisList.length > 0)
            this.yAxisList.push(newYAxis);
          }
          signal.yAxis = this.yAxisList.findIndex(item => item.title.text === signal.unit);
          signal.chartData = data[index].map(item => [this.timezoneService.unApplyTzFromDate(item.Timestamp).valueOf(), item[keyFieldName]]);
        }
        return signal;
      });
      this.signalsData.data.unshift(...this.addSeriesList);
      this.signalsData = {
        data: this.signalsData.data.filter(item => item.visible),
        yAxis: this.yAxisList
      };
      this.hasValidData = this.validatedData();
      this.updateYAxisMaxAndMin();
      this.createSeries();
      this.loadingSerie = false;
    });
  }

  private getListObs(list) {
    const chartData$: Observable<any>[] = list?.map(item => 
      this.getSignalData(
        { assetId: item.assetId, name: item.parkName, deviceType: item.deviceType },
        item.signal,
        item.granularity,
        item.queryData,
        this.startTs,
        this.endTs
      ));
    return combineLatest(chartData$)
  }

  private getSignalData({assetId, name, deviceType}, signal, granularity, queryType, startTS, endTS) {
    const signal$ = of([signal]);
    const options$ = of({ granularity, queryType, startTS, endTS });
    return deviceType === 'controlSystem' ? this.dataStudioRedesignService.requestSingleControlSystem(of([assetId]), signal$, options$, true) :
      deviceType === DEVICE_TYPES.METMAST ? this.dataStudioRedesignService.requestMetmast([assetId], signal, ({ granularity, queryType, startTS, endTS }), true) :
      this.dataStudioRedesignService.requestSingleTurbine(of({ _id: assetId, name }), signal$, options$, of(true));
  }

  private createSeries() {
    this.dataAnalysisService.timeSeriesSignalAdd$.next(true);
    this.updateFlag = true;
    this.addSeriesList = [];
    this.loadingUpdate = false;
  }

  private updateSeries() {
    this.updateFlag = true;
    this.loadingUpdate = false;
  }

  private setYAxis(series: ISeries[]) {
    const yAxisUnits = [...new Set(series.filter(s => s.visible).map(s => s.unit))];
    const yAxis = series.filter(s => s.visible).map((serie, i) => {
      return this.setYAxisValue(serie, serie.unit !== yAxisUnits[0])
    });

    return uniqWith(yAxis, (objA, objB) =>
      isEqual(objA.title, objB.title)
    );
  }

  private setYAxisValue(serie: ISeries, opposite) {
    return {
      ...HIGHCHART_OPTIONS_DEFAULT.yAxis,
      labels: {
        style: {
          color: 'var(--font)'
        }
      },
      tickAmount: 6,
      tickPosition: [],
      min: serie.yAxisMin,
      max: serie.yAxisMax,
      title: {
        text: `${serie.unit}`,
        style: {
          color: 'var(--font)'
        }
      },
      opposite: false, //TODO
      showEmpty: false
    }
  }

  private getQueryData() {
    this.loadingUpdate = true;
    this.dataStudioService.getQueryDetails(this.queryId)
    .pipe(takeUntil(this.unsubscribe$), tap(() => { 
      this.data = null;
      this.series = [];
      this.signalsData = { data: [], yAxis: [] };
      this.hasValidData = false;
    }))
    .subscribe((d: IDataStudioQuery) => {
      this.data = d;
      const { granularity, period, startTs, endTs, series } = d;
      Object.assign(this, { granLabel: granularity, periodLabel: period, startTs, endTs });
      this.getListObs(series).subscribe((data: any[]) => {
        this.yAxisList = this.setYAxis(series);
        this.series = series.map((signal, index) => {
          const keyFieldName = this.getKeyFieldName(signal, data[index]);
          if (data[index].length) {
            signal.yAxis = this.yAxisList.findIndex(item => item.title.text === signal.unit);
            if (data[index].every(d => isNil(d[keyFieldName]))) {
              signal.chartData = []  
            } else {
              signal.chartData = data[index].map(item => [ this.timezoneService.unApplyTzFromDate(item.Timestamp).valueOf(), item[keyFieldName]]);
            }
          }

          return signal
        });
        this.seriesIds = this.series.map(serie => serie.id);
        if (this.activeView === 0) {
          this.signalsData = {
            data: this.series.filter(item => item.visible),
            yAxis: this.yAxisList
          };
          this.hasValidData = this.validatedData();
        }
        this.granOptions = this.filterGranularityOptions();
        this.loadingSerie = false;
        this.loadingChart = false;
        this.filteredSeries = cloneDeep(this.series);
        this.loadingSignalList = false;
        this.lastIndex = Math.max(...this.series.filter(item => item.visible).map(item => item.yAxis));
        this.updateYAxisMaxAndMin();
        this.loadingUpdate = false;
      })
      this.granValue = this.granOptions.values[this.granOptions.names.findIndex(name => name === granularity)];
    });
  }

  private getTicks(dataMin, dataMax) {
    let tick = dataMin;
    const positions = [];
    const increment = (dataMax - dataMin) / 5;
    if (dataMax !== null && dataMin !== null) {
      for (tick; tick - increment < dataMax; tick += increment) {
        positions.push(Number(tick.toFixed(tick < 10 ? 4 : 0)));
      }
    }
    return positions;
  }

  private updateYAxisMaxAndMin() {
    this.series.forEach(serie => {
      serie.yAxisMin = this.signalsData.data.find(d => d.assetId === serie.assetId && d.signal === serie.signal) ? this.signalsData.data.find(d => d.assetId === serie.assetId && d.signal === serie.signal)?.yAxisMin : serie.yAxisMin;
      serie.yAxisMax = this.signalsData.data.find(d => d.assetId === serie.assetId && d.signal === serie.signal) ? this.signalsData.data.find(d => d.assetId === serie.assetId && d.signal === serie.signal)?.yAxisMax : serie.yAxisMax;
      if (this.series.every(s => !s.yAxisMin && !s.yAxisMax)) {
        this.yAxisList.forEach(y => {y.min = null; y.max = null; y.tickPositions = null})
      } else {
        const seriesSharingYAxis = this.series.filter(s => s.yAxis === serie.yAxis);
        const values = seriesSharingYAxis.map(s => s.chartData.map(v => v[1]).flat()).flat();
        const dataMin = seriesSharingYAxis.some(s => s.yAxisMin !== null && s.yAxisMin !== undefined) ? seriesSharingYAxis.map(s => s.yAxisMin).filter(s => s !== null && s !== undefined)[0] : Math.min(...values);
        const dataMax = seriesSharingYAxis.some(s => s.yAxisMax !== null && s.yAxisMax !== undefined) ? seriesSharingYAxis.map(s => s.yAxisMax).filter(s => s !== null && s !== undefined)[0] : Math.max(...values);
        if (seriesSharingYAxis.length > 0 &&
          (serie.yAxisMin !== seriesSharingYAxis.map(s => s.yAxisMin).filter(s => s !== null && s !== undefined)[0] ||
          serie.yAxisMax !== seriesSharingYAxis.map(s => s.yAxisMax).filter(s => s !== null && s !== undefined)[0])
        ) {
          serie.yAxisMin = seriesSharingYAxis.map(s => s.yAxisMin).filter(s => s !== null && s !== undefined)[0];
          serie.yAxisMax = seriesSharingYAxis.map(s => s.yAxisMax).filter(s => s !== null && s !== undefined)[0];
        }
        const index = this.yAxisList.findIndex(y => y.title.text === serie.unit);
        const existingYAxisValue = (serie.yAxisMin !== null && serie.yAxisMin !== undefined) || (serie.yAxisMax !== null && serie.yAxisMax !== undefined);
        this.yAxisList[index] = {
          ...this.yAxisList[index],
          min: existingYAxisValue ? dataMin : null,
          max: existingYAxisValue ? dataMax : null,
          tickPositions: existingYAxisValue ? this.getTicks(dataMin, dataMax) : null
        }
      }
    })
  }

  private setDefaultData() {
    this.data = {...newQueryDefaultParams, id: null};
    const { granularity, period, startTs, endTs, series } = this.data;
    Object.assign(this, { granLabel: granularity, periodLabel: period, startTs, endTs, series });
    this.loadingChart = false;
    this.loadingSignalList = false;
    this.addSeries();
  }

  private createQuery(query) {
    this.dataStudioService.createQuery(query).subscribe((response) => {
      if (response?.data?.createDataAnalysisQuery?._id) {
        this.queryId = response.data.createDataAnalysisQuery._id;
        this.myQueriesView = true;
        (this.data.id as any) = response.data.createDataAnalysisQuery.queryNumber;
      }
      this.updateFlag = false;
    });
  }

  private updateQuery(query) {
    this.dataStudioService.updateQuery(query).subscribe(() => {
      this.updateFlag = false;
    });
  }

  public showConfirmPopup(template: TemplateRef<any>, mode: 'back' | 'cancel'): void {
    if (!this.updateFlag) {
      if (mode === 'back') this.backToQueriesList();
      if (mode === 'cancel') this.cancel();
      return;
    }
    this.modalRef = this.modalService.show(template, {
        backdrop: true,
        keyboard: false,
        class: `modal-sm modal-dialog-centered ${this.themeService.getTheme().value}`
    });
  }

  public closeModal(): void {
    this.modalRef?.hide();
  }

  public exportChart(event): void {
    this.exportChart$.next(event.index);
  }

  private filterGranularityOptions(): {names: string[], values: string[]} {
    const dayDifference = moment(this.endTs).diff(moment(this.startTs), 'days') + 1;
    let filterIndex = 2;
    if (dayDifference > 6) {
      filterIndex = 3;
      if (dayDifference > 29) {
        filterIndex = 4;
        if (dayDifference > 364) {
          filterIndex = 5;
        } 
      }
    }
    return {
      names: GRANULARITY_OPTIONS.names.filter((v, i) => i < filterIndex),
      values: GRANULARITY_OPTIONS.values.filter((v, i) => i < filterIndex)
    };
  }

  private validatedData(){
    return this.signalsData.data.length > 0
            && this.signalsData.data.some(s => !!s.chartData?.length)
            && this.signalsData.data.some(s => s.chartData?.some(c => c[1] !== null && c[1] !== undefined));
  }
}
